import { createSlice } from '@reduxjs/toolkit';
import { SearchStatus, FiltersInitialValue } from '../../constants';
import generateUniqueId from '../../utils/generateUniqueId';

const initialState = {
  flightSearches: {}, // contains { <tripId>: { searchObj: {}} } tripid comes from cruise
  responses: {},
  error: {},
  flightSeatMaps: {}, // contains { <flight key>: { <flight seatmap response> }}
  validateFlightStatus: {}, // contains { <flight key>: { status: <status>, error: <error> } }
  bookingClasses: {},
  airFareRulesStatus: {},
  airFareRulesErrors: {},
  airFareRulesList: {},
};

const flightSlice = createSlice({
  name: 'flight',
  initialState,
  reducers: {
    updateFlightSearchObj: (state, {
      payload: {
        tripId, searchStatus, searchObj, defaultSearchObj, airlinesAutocompleteResults,
        hasUserInteractedWithFlightList, searchProgress, foundTotalFlights, requestId,
      },
    }) => ({
      ...state,
      flightSearches: {
        ...state.flightSearches,
        ...{
          [tripId]: {
            ...state.flightSearches[tripId],
            searchStatus: searchStatus === undefined
              ? (state.flightSearches[tripId] || {}).searchStatus
              : (searchStatus || SearchStatus.INITIAL),
            searchObj: {
              ...FiltersInitialValue.flight,
              ...(state.flightSearches[tripId] || {}).searchObj,
              ...searchObj,
            },
            searchObjRecentSearch: searchStatus === SearchStatus.SEARCHING ? {
              ...FiltersInitialValue.flight,
              ...(state.flightSearches[tripId] || {}).searchObj,
              ...searchObj,
            } : (state.flightSearches[tripId] || {}).searchObjRecentSearch,
            defaultSearchObj: {
              ...FiltersInitialValue.flight,
              ...(state.flightSearches[tripId] || {}).defaultSearchObj,
              ...defaultSearchObj,
            },
            airlinesAutocompleteResults: airlinesAutocompleteResults !== undefined
              ? airlinesAutocompleteResults
              : ((state.flightSearches || {})[tripId] || {}).airlinesAutocompleteResults,
            hasUserInteractedWithFlightList: hasUserInteractedWithFlightList !== undefined
              ? hasUserInteractedWithFlightList
              : (state.flightSearches[tripId] || {}).hasUserInteractedWithFlightList,
            searchProgress: searchProgress !== undefined
              ? searchProgress : (state.flightSearches[tripId] || {}).searchProgress,
            foundTotalFlights: foundTotalFlights !== undefined
              ? foundTotalFlights : (state.flightSearches[tripId] || {}).foundTotalFlights,
            requestId: requestId !== undefined
              ? requestId : (state.flightSearches[tripId] || {}).requestId,
          },
        },
      },
    }),
    flightSearch: (state) => ({
      ...state,
    }),
    flightSuccess: (state, {
      payload: {
        queryString, response, tripId, airlinesAutocompleteResults, searchObj, defaultSearchObj,
      },
    }) => ({
      ...state,
      responses: {
        ...state.responses,
        [queryString]: {
          ...response,
        },
      },
      flightSearches: {
        ...state.flightSearches,
        [tripId]: {
          ...state.flightSearches[tripId],
          queryString,
          searchStatus: SearchStatus.DONE,
          airlinesAutocompleteResults,
          searchObj: {
            ...state.flightSearches[tripId].searchObj,
            ...searchObj,
          },
          defaultSearchObj: {
            ...state.flightSearches[tripId].defaultSearchObj,
            ...defaultSearchObj,
          },
        },
      },
    }),
    flightFailure: (state) => ({
      ...state,
    }),
    initFlightSearchRequest: (state) => ({
      ...state,
      requestId: state && state.payload && state.payload.isNewSearch
        ? generateUniqueId() : state.requestId,
    }),
    pollingFlightSearchRequest: (state) => ({
      ...state,
    }),
    getFlightSeatmapRequest: (state, { payload: { key } }) => ({
      ...state,
      flightSeatMaps: {
        ...state.flightSeatMaps,
        [key]: {
          getFlightSeatmapStatus: SearchStatus.SEARCHING,
          getSeatmapError: null,
        },
      },
    }),
    getFlightSeatmapSuccess: (state, { payload: { error, responseData, key } }) => ({
      ...state,
      flightSeatMaps: {
        ...state.flightSeatMaps,
        [key]: {
          getFlightSeatmapStatus: SearchStatus.DONE,
          getSeatmapError: error,
          ...responseData,
        },
      },
    }),
    validateFlightSegmentsRequest: (state, { payload: { flightKey } }) => ({
      ...state,
      validateFlightStatus: {
        ...state.validateFlightStatus,
        [flightKey]: {
          status: SearchStatus.SEARCHING,
        },
      },
    }),
    validateFlightSegmentsResponse: (state, { payload: { flightKey, error } }) => ({
      ...state,
      validateFlightStatus: {
        ...state.validateFlightStatus,
        [flightKey]: {
          status: SearchStatus.DONE,
          error,
        },
      },
    }),
    initDirectSellFlightSearchRequest: (state) => ({
      ...state,
    }),
    getCheckAirAvailabilityRequest: (state, { payload: { key } }) => ({
      ...state,
      bookingClasses: {
        ...state.bookingClasses,
        [key]: {
          checkAirAvailabilityStatus: SearchStatus.SEARCHING,
          checkAirAvailabilityError: null,
        },
      },
    }),
    getCheckAirAvailabilitySuccess: (state, { payload: { responseData, error, key } }) => ({
      ...state,
      bookingClasses: {
        ...state.bookingClasses,
        [key]: {
          bookingClasses: Object.values(responseData),
          checkAirAvailabilityStatus: SearchStatus.DONE,
          checkAirAvailabilityError: error,
        },
      },
    }),
    getAirFareRulesRequest: (state, { payload: { tripId } }) => ({
      ...state,
      airFareRulesStatus: {
        ...state.airFareRulesStatus,
        [tripId]: SearchStatus.SEARCHING,
      },
    }),
    getAirFareRulesResponse: (state, {
      payload: {
        tripId, error, errorCode, airFareRulesList,
      },
    }) => ({
      ...state,
      airFareRulesStatus: {
        ...state.airFareRulesStatus,
        [tripId]: error ? SearchStatus.ERROR : SearchStatus.DONE,
      },
      airFareRulesErrors: {
        ...state.airFareRulesErrors,
        [tripId]: error ? { message: error, code: errorCode } : null,
      },
      airFareRulesList: {
        ...state.airFareRulesList,
        [tripId]: airFareRulesList || [],
      },
    }),
  },
});

export const {
  flightSearch, flightSuccess, flightFailure, updateFlightSearchObj,
  getFlightSeatmapRequest,
  getFlightSeatmapSuccess, pollingFlightSearchRequest,
  initFlightSearchRequest, validateFlightSegmentsRequest,
  validateFlightSegmentsResponse, initDirectSellFlightSearchRequest,
  getCheckAirAvailabilityRequest, getCheckAirAvailabilitySuccess,
  getAirFareRulesRequest, getAirFareRulesResponse,
} = flightSlice.actions;
export default flightSlice.reducer;
